/**
 * Recurring Expense Service
 * 
 * Manages recurring expense templates and auto-generation logic.
 * Templates define the pattern (amount, frequency, category, etc.)
 * while actual expenses are generated when due dates arrive.
 */

import { supabase } from '../../database/supabase-client';
import { requireAuth } from '../../utils/auth';
import type {
    RecurringExpense,
    CreateRecurringExpenseDto,
    UpdateRecurringExpenseDto,
    RecurringFrequency,
    Expense
} from '../types';
import {
    calculateNextDueDate,
    shouldGenerateExpense,
    createExpenseFromTemplate,
    getCategoryName,
    expenseAlreadyExists,
    updateNextDueDate,
    deactivateTemplate,
} from './recurring-helpers';

export class RecurringExpenseService {
    /**
     * Get all recurring expense templates for current user
     */
    static async getRecurringExpenses(): Promise<RecurringExpense[]> {
        const user = await requireAuth();

        const { data, error } = await supabase
            .from('recurring_expenses')
            .select('*')
            .eq('user_id', user.id)
            .order('next_due_date', { ascending: true });

        if (error) throw error;

        return data.map(row => this.mapToRecurringExpense(row));
    }

    /**
     * Get active recurring expenses (ready for auto-generation check)
     */
    static async getActiveRecurringExpenses(): Promise<RecurringExpense[]> {
        const user = await requireAuth();

        const { data, error } = await supabase
            .from('recurring_expenses')
            .select('*')
            .eq('user_id', user.id)
            .eq('is_active', true)
            .order('next_due_date', { ascending: true });

        if (error) throw error;

        return data.map(row => this.mapToRecurringExpense(row));
    }

    /**
     * Get upcoming recurring expenses (next 30 days)
     */
    static async getUpcomingRecurringExpenses(days: number = 30): Promise<RecurringExpense[]> {
        const user = await requireAuth();

        const today = new Date();
        const futureDate = new Date();
        futureDate.setDate(today.getDate() + days);

        const { data, error } = await supabase
            .from('recurring_expenses')
            .select('*')
            .eq('user_id', user.id)
            .eq('is_active', true)
            .lte('next_due_date', futureDate.toISOString().split('T')[0])
            .order('next_due_date', { ascending: true });

        if (error) throw error;

        return data.map(row => this.mapToRecurringExpense(row));
    }

    /**
     * Create a new recurring expense template
     */
    static async createRecurringExpense(dto: CreateRecurringExpenseDto): Promise<RecurringExpense> {
        const user = await requireAuth();

        // Calculate next due date (starts on start_date)
        const nextDueDate = dto.startDate;

        const { data, error } = await supabase
            .from('recurring_expenses')
            .insert({
                user_id: user.id,
                amount: dto.amount,
                currency: dto.currency,
                category_id: dto.categoryId,
                description: dto.description,
                frequency: dto.frequency,
                start_date: dto.startDate,
                end_date: dto.endDate || null,
                next_due_date: nextDueDate,
                is_active: true
            })
            .select()
            .single();

        if (error) throw error;

        return this.mapToRecurringExpense(data);
    }

    /**
     * Update an existing recurring expense template
     */
    static async updateRecurringExpense(
        id: string,
        dto: UpdateRecurringExpenseDto
    ): Promise<RecurringExpense> {
        const user = await requireAuth();

        // Get current template to check for frequency changes
        const { data: current, error: fetchError } = await supabase
            .from('recurring_expenses')
            .select('*')
            .eq('id', id)
            .eq('user_id', user.id)
            .single();

        if (fetchError) throw fetchError;

        const updateData: any = {
            updated_at: new Date().toISOString()
        };

        if (dto.amount !== undefined) updateData.amount = dto.amount;
        if (dto.currency !== undefined) updateData.currency = dto.currency;
        if (dto.categoryId !== undefined) updateData.category_id = dto.categoryId;
        if (dto.description !== undefined) updateData.description = dto.description;
        if (dto.endDate !== undefined) updateData.end_date = dto.endDate || null;
        if (dto.isActive !== undefined) updateData.is_active = dto.isActive;

        // If frequency changed, recalculate next_due_date from start_date
        if (dto.frequency !== undefined && dto.frequency !== current.frequency) {
            updateData.frequency = dto.frequency;
            // Recalculate next due from current next_due_date with new frequency
            updateData.next_due_date = calculateNextDueDate(current.next_due_date, dto.frequency);
        }

        const { data, error } = await supabase
            .from('recurring_expenses')
            .update(updateData)
            .eq('id', id)
            .eq('user_id', user.id)
            .select()
            .single();

        if (error) throw error;

        return this.mapToRecurringExpense(data);
    }

    /**
     * Delete a recurring expense template
     */
    static async deleteRecurringExpense(id: string): Promise<void> {
        const user = await requireAuth();

        const { error } = await supabase
            .from('recurring_expenses')
            .delete()
            .eq('id', id)
            .eq('user_id', user.id);

        if (error) throw error;
    }

    /**
     * Pause/Resume a recurring expense template
     */
    static async toggleActive(id: string): Promise<RecurringExpense> {
        const user = await requireAuth();

        // First get current state
        const { data: current, error: fetchError } = await supabase
            .from('recurring_expenses')
            .select('is_active')
            .eq('id', id)
            .eq('user_id', user.id)
            .single();

        if (fetchError) throw fetchError;

        // Toggle it
        const { data, error } = await supabase
            .from('recurring_expenses')
            .update({ is_active: !current.is_active })
            .eq('id', id)
            .eq('user_id', user.id)
            .select()
            .single();

        if (error) throw error;

        return this.mapToRecurringExpense(data);
    }

    /**
     * Auto-generate expenses from due recurring templates
     * Returns count of expenses created
     * 
     * Process:
     * 1. Fetch all active templates with due dates <= today
     * 2. For each template:
     *    - Skip if expired (deactivate it)
     *    - Skip if expense already exists for this date
     *    - Create expense from template
     *    - Calculate and update next due date
     */
    static async generateDueExpenses(): Promise<number> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const today = new Date().toISOString().split('T')[0];

        // Get all active recurring expenses that are due
        const { data: dueTemplates, error: fetchError } = await supabase
            .from('recurring_expenses')
            .select('*')
            .eq('user_id', user.id)
            .eq('is_active', true)
            .lte('next_due_date', today);

        if (fetchError) throw fetchError;
        if (!dueTemplates || dueTemplates.length === 0) return 0;

        let generatedCount = 0;

        for (const template of dueTemplates) {
            try {
                // Skip if template has ended (and deactivate it)
                if (template.end_date && template.next_due_date > template.end_date) {
                    await deactivateTemplate(template.id);
                    continue;
                }

                // Check if expense already exists for this template + date (prevent duplicates)
                const exists = await expenseAlreadyExists(
                    user.id,
                    template.id,
                    template.next_due_date
                );

                if (exists) {
                    // Expense already generated, just update next_due_date
                    const nextDueDate = calculateNextDueDate(
                        template.next_due_date,
                        template.frequency
                    );
                    await updateNextDueDate(template.id, nextDueDate);
                    generatedCount++; // Count as processed
                    continue;
                }

                // Fetch category name for legacy field
                const categoryName = await getCategoryName(template.category_id);

                // Create expense from template
                await createExpenseFromTemplate(template, categoryName);

                // Calculate and update next due date
                const nextDueDate = calculateNextDueDate(
                    template.next_due_date,
                    template.frequency
                );
                await updateNextDueDate(template.id, nextDueDate);

                generatedCount++;
            } catch (err) {
                console.error(`Error processing recurring template ${template.id}:`, err);
            }
        }

        return generatedCount;
    }

    /**
     * Get expenses generated from a specific template
     */
    static async getExpensesFromTemplate(templateId: string): Promise<Expense[]> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const { data, error } = await supabase
            .from('expenses')
            .select('*')
            .eq('user_id', user.id)
            .eq('recurring_template_id', templateId)
            .order('date', { ascending: false });

        if (error) throw error;

        return data.map(row => ({
            id: row.id,
            userId: row.user_id,
            amount: parseFloat(row.amount),
            currency: row.currency,
            date: row.date,
            category: row.category || '',
            categoryId: row.category_id,
            description: row.description,
            createdAt: row.created_at,
            updatedAt: row.updated_at,
            recurringTemplateId: row.recurring_template_id,
            isAutoGenerated: row.is_auto_generated
        }));
    }

    /**
     * Get frequency display name
     */
    static getFrequencyDisplayName(frequency: RecurringFrequency): string {
        const names: Record<RecurringFrequency, string> = {
            daily: 'Daily',
            weekly: 'Weekly',
            monthly: 'Monthly',
            yearly: 'Yearly'
        };
        return names[frequency];
    }

    /**
     * Map database row to RecurringExpense type
     */
    private static mapToRecurringExpense(row: any): RecurringExpense {
        return {
            id: row.id,
            userId: row.user_id,
            amount: parseFloat(row.amount),
            currency: row.currency,
            categoryId: row.category_id,
            category: row.category,
            description: row.description,
            frequency: row.frequency,
            startDate: row.start_date,
            endDate: row.end_date,
            nextDueDate: row.next_due_date,
            isActive: row.is_active,
            createdAt: row.created_at,
            updatedAt: row.updated_at
        };
    }
}
